import { Autocomplete, Divider, FormControl, LinearProgress, ListItemIcon, ListItemText, MenuItem, TextField } from '@mui/material'
import { useState, useEffect, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useFarmList } from '../farm/use-farm-list'
import { setLastFarmId, setLastSeasonId, useOrgContext, useOrgContextDispatch } from '~/common/context/organization/org-context'
import { cacheManager } from '~/api/cache-manager'
import { useUpdateUser } from '~/api/supabase-mutations'
import { useCurrentUser } from '~/common/use-current-user'
import { useFeatures } from '~/common/use-features'
import { SELECTOR_MIN_WIDTH_PX, SELECTOR_WIDTH_VW } from '~/common/constants/layout-constants'
import { AddFarmModal } from '../farm/add-farm/add-farm-modal'
import { AddHomeTwoTone } from '@mui/icons-material'
import { matchSorter } from 'match-sorter'

export const FarmSelector = ({ lightBackground, usePortal = false, onSelect }) => {
  const dispatch = useOrgContextDispatch()
  const { activeOrgs: [ contextFarmOrg ] } = useOrgContext()
  const contextFarmOrgId = contextFarmOrg?.id || null
  const [farmId, setFarmId] = useState(contextFarmOrgId)
  const [showFarmModal, setShowFarmModal] = useState(false)

  const { farmList, loading, error, refetch } = useFarmList()
  const { currentUser } = useCurrentUser()
  const { features } = useFeatures()
  const { updateUser } = useUpdateUser()
  const [, setSearchParams] = useSearchParams()

  const strokeColor = lightBackground ? 'primary.main' : 'white'

  // Fuzzy search filter for farms
  const filterFarmOptions = (options, { inputValue }) => {
    // Don't filter special options (None and Add New Farm)
    if (!inputValue) return options

    const filtered = matchSorter(
      options.filter(opt => opt !== null && opt.id !== 'add-new-farm'),
      inputValue,
      { keys: ['name'] }
    )

    // Always include None at start and Add New Farm at end
    return [
      options.find(opt => opt === null),
      ...filtered,
      options.find(opt => opt?.id === 'add-new-farm')
    ].filter(Boolean)
  }

  // Build options array: null (None), ...farms, sentinel for "Add New Farm"
  const options = useMemo(() => [
    null,
    ...farmList,
    { id: 'add-new-farm', name: 'Add New Farm' }
  ], [farmList])

  // Get current selected value object
  const selectedValue = useMemo(() => {
    if (!farmId) return null
    return farmList.find(farm => farm.id === farmId) || null
  }, [farmId, farmList])

  // Sync local state with URL when URL changes
  useEffect(() => {
    setFarmId(contextFarmOrgId)
  }, [contextFarmOrgId])

  // Refetch farm list if contextFarmOrgId references a farm not in current list
  // This handles the case where a new farm is created outside the selector
  useEffect(() => {
    if (contextFarmOrgId && !loading) {
      const farmExists = farmList.some(farm => farm.id === contextFarmOrgId)
      if (!farmExists) {
        refetch()
      }
    }
  }, [contextFarmOrgId, farmList, loading, refetch])

  // Auto-select the only farm when there is exactly one and nothing is selected
  useEffect(() => {
    if (!loading && farmList.length === 1 && !farmId) {
      handleChange(null, farmList[0])
    }
  }, [loading, farmList, farmId]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = async (event, newValue) => {
    // Handle "Add New Farm" click
    if (newValue?.id === 'add-new-farm') {
      setShowFarmModal(true)
      return
    }

    const selectedFarmId = newValue?.id || ''
    setFarmId(selectedFarmId)
    onSelect?.()

    // Persist to database for cross-login restoration
    if (currentUser?.id) {
      await updateUser(currentUser.id, {
        last_selected_farm_id: selectedFarmId || null
      })
    }

    // invalidate all caches, essentially a new login once a new farm is selected
    cacheManager.invalidateAll()

    // Reset season selection when farm changes
    dispatch(setLastSeasonId({ seasonId: null }))

    // Save to context and url for current session
    if (selectedFarmId) {
      dispatch(setLastFarmId({ farmId: selectedFarmId }))
      setSearchParams({ farmId: selectedFarmId })
    } else {
      dispatch(setLastFarmId({ farmId: null }))
      setSearchParams(params => {
        const newParams = { ...params }
        delete newParams.farmId
        return newParams
      })
    }
  }

  if (!features.farm_selector) return null

  if (loading) {
    return <LinearProgress />
  }

  return (
    <FormControl
      size='small'
      sx={{
        zIndex: (theme) => theme.zIndex.drawer + 3,
        '& .MuiFormLabel-root': {
          color: strokeColor,
        },
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: strokeColor,
        },
        '& .MuiOutlinedInput-root': {
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: strokeColor,
            borderWidth: 2,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: strokeColor,
            borderWidth: 2,
          }
        }
      }}
    >
      <Autocomplete
        size='small'
        disablePortal={!usePortal}
        options={options}
        value={selectedValue}
        onChange={handleChange}
        filterOptions={filterFarmOptions}
        getOptionLabel={(option) => {
          if (!option) return 'None'
          if (option.id === 'add-new-farm') return 'Add New Farm'
          return option.name || ''
        }}
        isOptionEqualToValue={(option, value) => {
          if (!option && !value) return true
          if (!option || !value) return false
          return option.id === value.id
        }}
        renderOption={(props, option) => {
          const { key: _key, ...otherProps } = props

          // None option
          if (!option) {
            return (
              <MenuItem key="none" {...otherProps}>
                <em>None</em>
              </MenuItem>
            )
          }

          // Add New Farm option
          if (option.id === 'add-new-farm') {
            return (
              <div key="add-new-farm">
                <Divider />
                <MenuItem {...otherProps}>
                  <ListItemIcon>
                    <AddHomeTwoTone fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>{option.name}</ListItemText>
                </MenuItem>
              </div>
            )
          }

          // Regular farm option
          return (
            <MenuItem key={option.id} {...otherProps}>
              {option.name}
            </MenuItem>
          )
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Select Farm"
            error={!!error}
            sx={{
              '& .MuiFormLabel-root': {
                color: `${strokeColor} !important`, // pesky "Select Farm" label coloring is overridden in many places
              },
              '& .MuiInputBase-input': {
                color: strokeColor,
              }
            }}
          />
        )}
        sx={{
          width: SELECTOR_WIDTH_VW,
          minWidth: SELECTOR_MIN_WIDTH_PX,
          color: strokeColor,
          '& .MuiSvgIcon-root': {
            color: strokeColor,
          },
          '& .MuiInputLabel-root': {
            color: strokeColor,
          },
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: strokeColor,
          },
          '& .MuiOutlinedInput-root': {
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: `${strokeColor} !important`,
              borderWidth: 2,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: `${strokeColor} !important`,
              borderWidth: 2,
            }
          }
        }}
      />
      <AddFarmModal open={showFarmModal} onClose={() => setShowFarmModal(false)} />
    </FormControl>
  )
}